import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid-pro';
import JSONEditor from '@src/components/_elements/JSONEditor/JSONEditor';
import QueryKeys from '@src/constants/queryKeys';
import usePointConfigMutations from '@src/hooks/api/mutations/usePointConfigMutations';
import { CollectionPayload } from '@src/types/Collection';
import {
  PointConfigEdit,
  PointConfigEditable,
  PointConfigExtended,
  ThresholdValue,
} from '@src/types/PointConfig';
import { useQueryClient } from '@tanstack/react-query';
import { validateThresholds } from '@utils/thresholdUtils';
import { Dispatch, SetStateAction, useState } from 'react';
import pointConfigSx from '../configs/pointConfigSx';

interface DialogProps {
  jsonData: PointConfigExtended;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
  handleCancel: (id?: GridRowId) => () => void;
  setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
}

function PointConfigDialog({
  jsonData,
  handleCancel,
  setOpen,
  setRowModesModel,
  setJsonPayload,
}: DialogProps) {
  const alias = useAlias();
  const {
    id,
    remove,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    pointAlias,
    ...rest
  } = jsonData;
  const [newData, setNewData] = useState<PointConfigEdit | PointConfigEditable>(
    id ? rest : { ...rest, pointAlias },
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const getTitle = () => {
    if (remove) {
      return alias('Are you sure you want to delete this config?');
    }

    if (id) {
      return alias('Edit Point Config');
    }

    return alias('Add Point Config');
  };

  const { createPointConfig, updatePointConfig, deletePointConfig } =
    usePointConfigMutations(jsonData.pointAlias);

  const handleSuccessCallback = () => {
    if (id !== undefined) {
      setRowModesModel((prevState) => ({
        ...prevState,
        [id]: { mode: GridRowModes.View },
      }));
    }

    queryClient.invalidateQueries({
      queryKey: [QueryKeys.pointConfigs],
    });
    setJsonPayload(null);
    setOpen(false);
  };

  const handlePCCreate = () =>
    createPointConfig.mutate(newData as PointConfigEdit, {
      onSuccess: handleSuccessCallback,
    });

  const handlePCUpdate = () =>
    updatePointConfig.mutate(newData as PointConfigEditable, {
      onSuccess: handleSuccessCallback,
    });

  const handlePCDelete = () =>
    deletePointConfig.mutate(
      {},
      {
        onSuccess: handleSuccessCallback,
      },
    );

  const handleConfirm = () => {
    setValidationError(null);

    const dataWithThresholds = newData as (
      | PointConfigEdit
      | PointConfigEditable
    ) & { thresholdValues?: ThresholdValue[] };
    if (
      dataWithThresholds.thresholdValues &&
      dataWithThresholds.thresholdValues.length > 0
    ) {
      const validation = validateThresholds(dataWithThresholds.thresholdValues);

      if (!validation.valid) {
        setValidationError(
          validation.error || alias('Invalid threshold configuration'),
        );
        return;
      }

      dataWithThresholds.thresholdValues = validation.sorted;
    }

    if (remove !== undefined) {
      return handlePCDelete();
    }
    if (id) {
      return handlePCUpdate();
    }
    return handlePCCreate();
  };

  return (
    <Dialog
      open
      PaperProps={
        !remove
          ? {
              sx: pointConfigSx.pcDialogPaper,
            }
          : {}
      }
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      {!remove && (
        <DialogContent>
          {validationError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}
          <JSONEditor
            json={newData}
            onChange={setNewData}
            options={{ mode: 'code' }}
          />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleCancel(id)}>
          <FormattedAlias id='Cancel' />
        </Button>
        <Button size='small' variant='contained' onClick={handleConfirm}>
          <FormattedAlias id='Confirm' />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PointConfigDialog;
